import type { HelpTextProps } from '@react-types/shared/src/inputs';
import type { ReactNode } from 'react';
import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { FieldPropsWithElement } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { Badge } from '../../components/Badge/Badge';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { styled } from '../../stitches.config';
import { VStack } from '../../utilities/Stack/VStack';
import {
  type AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { ControlProvider } from '../shared/context';
import { ErrorMessages, useErrorMessages } from './common/ErrorMessageRegistry';
import { FieldAnchorTarget, useFieldAnchor } from './common/FieldAnchor';
import { FieldCompositeProvider } from './common/FieldCompositeProvider';
import { FieldDivider, fieldDividerDisplay } from './common/FieldDivider';
import { SecondaryFieldDescription, SecondaryFieldLabel } from './common/FieldLabel';
import { useDescriptionAndErrorIds } from './common/useDescriptionAndErrorIds';
import { useFieldElement } from './common/useFieldElement';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';
import { FieldErrorMessages } from './FieldError';
import { RegisterFieldError } from './FieldError/FieldErrorContext';

const SecondaryFieldContainer = styled('div', FieldAnchorTarget, {
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '$10 $16',
  [fieldDividerDisplay]: 'none',

  '&:not(:last-child)': {
    paddingBottom: 0,
    [fieldDividerDisplay]: 'flex',
  },
});
SecondaryFieldContainer.displayName = 'SecondaryFieldContainer';

const SecondaryFieldDetails = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  gap: '$2',
});
SecondaryFieldDetails.displayName = 'SecondaryFieldDetails';

const SecondaryFieldHeader = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
});
SecondaryFieldHeader.displayName = 'SecondaryFieldHeader';

const SecondaryFieldBadges = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
SecondaryFieldBadges.displayName = 'SecondaryFieldBadges';

const SecondaryFieldLabelling = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});
SecondaryFieldLabelling.displayName = 'SecondaryFieldLabelling';

const SecondaryFieldControls = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
SecondaryFieldControls.displayName = 'SecondaryFieldControls';

export const SecondaryFieldFields = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
SecondaryFieldFields.displayName = 'SecondaryFieldFields';

const SecondaryFieldAlert = styled(Alert, {
  padding: '$8 $16',
});
SecondaryFieldAlert.displayName = 'SecondaryFieldAlert';

interface SecondaryFieldProps extends FieldPropsWithElement {
  badges?: React.ReactNode;
  controls?: React.ReactNode;
  /**
   * Marks the field as optional with a badge.
   */
  optional?: boolean;
  children?: React.ReactNode;
}

export function SecondaryField(props: SecondaryFieldProps) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();
  const anchor = useFieldAnchor(customFieldProps.name);

  const {
    badges,
    label = customFieldProps.label,
    description = customFieldProps.description,
    errorMessage = customFieldProps.errorMessage,
    children,
    controls,
    element,
    accessLevel: accessLevelProp,
    internal,
    optional,
    ...remaining
  } = props;

  const {
    labelProps,
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
  } = useField({
    label,
    description,
    errorMessage,
  });

  const control = useFieldElement(element, mergeProps(inputProps, customInputProps));

  const accessLevel = useAccessLevelContext(accessLevelProp as AccessLevel);

  return (
    <MaybeFieldContainer>
      <SecondaryFieldContainer {...anchor.containerProps} {...remaining}>
        <VStack spacing={6}>
          <SecondaryFieldDetails>
            <SecondaryFieldHeader>
              <SecondaryFieldLabelling>
                <SecondaryFieldLabel {...labelProps}>{label}</SecondaryFieldLabel>
                {optional && (
                  <Badge ends="card" size="small" variant="neutral">
                    Optional
                  </Badge>
                )}
                {accessLevel
                  ? isAccessLevelVisible(accessLevel) && (
                      <FeatureBadge type={accessLevel} size="x-small" />
                    )
                  : internal && <FeatureBadge type="internal" size="x-small" />}
                {badges && (
                  <SecondaryFieldBadges>
                    <ControlProvider value={{ size: 'small' }}>{badges}</ControlProvider>
                  </SecondaryFieldBadges>
                )}
                {anchor.link}
              </SecondaryFieldLabelling>
              {controls && (
                <SecondaryFieldControls>
                  <ControlProvider value={{ size: 'small' }}>{controls}</ControlProvider>
                </SecondaryFieldControls>
              )}
            </SecondaryFieldHeader>
            {description && (
              <SecondaryFieldDescription {...descriptionProps}>
                {description}
              </SecondaryFieldDescription>
            )}
          </SecondaryFieldDetails>
          <SecondaryFieldFields>
            <ControlProvider value={{ size: 'small' }}>{control}</ControlProvider>
          </SecondaryFieldFields>
          {children}
        </VStack>
        <FieldDivider context="secondary" />
      </SecondaryFieldContainer>
      {customFieldProps.name && <RegisterFieldError name={customFieldProps.name} />}
      {errorMessage && (
        <FieldErrorMessages>
          <SecondaryFieldAlert
            {...errorMessageProps}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        </FieldErrorMessages>
      )}
    </MaybeFieldContainer>
  );
}

export type SecondaryFieldCompositeProps = {
  badges?: ReactNode;
  controls?: React.ReactNode;
  name?: string;
  label: ReactNode;
  fields: ReactNode;
  internal?: boolean;
  /**
   * Marks the field as optional with a badge.
   */
  optional?: boolean;
  /**
   * If true, multiple errors with the same message will not be shown.
   */
  deduplicateErrors?: boolean;
} & HelpTextProps;

export function SecondaryFieldComposite({
  badges,
  description,
  errorMessage,
  controls,
  fields,
  label,
  internal,
  optional,
  deduplicateErrors,
  name,
  ...remaining
}: SecondaryFieldCompositeProps) {
  const { messages, registerMessage, unregisterMessage } = useErrorMessages();
  const anchor = useFieldAnchor(name);

  const { descriptionId, errorMessageId, describedBy } = useDescriptionAndErrorIds(
    filterErrorMessage(errorMessage),
    description,
  );

  return (
    <FieldCompositeProvider
      registerMessage={registerMessage}
      unregisterMessage={unregisterMessage}
      describedBy={describedBy}
    >
      <SecondaryFieldContainer {...anchor.containerProps} {...remaining}>
        <VStack spacing={6}>
          <SecondaryFieldDetails>
            <SecondaryFieldHeader>
              <SecondaryFieldLabelling>
                <SecondaryFieldLabel>{label}</SecondaryFieldLabel>
                {optional && (
                  <Badge ends="card" size="small" variant="neutral">
                    Optional
                  </Badge>
                )}
                {internal && <FeatureBadge type="internal" size="x-small" />}
                {badges && (
                  <SecondaryFieldBadges>
                    <ControlProvider value={{ size: 'small' }}>{badges}</ControlProvider>
                  </SecondaryFieldBadges>
                )}
                {anchor.link}
              </SecondaryFieldLabelling>
              {controls && (
                <SecondaryFieldControls>
                  <ControlProvider value={{ size: 'small' }}>{controls}</ControlProvider>
                </SecondaryFieldControls>
              )}
            </SecondaryFieldHeader>
            {description && (
              <SecondaryFieldDescription id={descriptionId}>
                {description}
              </SecondaryFieldDescription>
            )}
          </SecondaryFieldDetails>
          <SecondaryFieldFields>
            <ControlProvider value={{ size: 'small' }}>{fields}</ControlProvider>
          </SecondaryFieldFields>
        </VStack>
        <FieldDivider context="secondary" />
      </SecondaryFieldContainer>
      {name && <RegisterFieldError name={name} />}
      <ErrorMessages messages={messages} deduplicate={deduplicateErrors} />
      {errorMessage && (
        <SecondaryFieldAlert
          id={errorMessageId}
          type="inline"
          relation="stacked"
          variant="negative"
          heading={filterErrorMessage(errorMessage)}
        />
      )}
    </FieldCompositeProvider>
  );
}
